import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import CtaBand from '@/components/CtaBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Check, Tablet, Receipt, Smartphone, Printer, Mail } from 'lucide-react'
import { hardwareCategories } from '@/content/hardware'
import { hardwarePage } from '@/content/hardwarePage'
import { heroImageOf, sectionImageOf } from '@/content/applyImages'

function CapabilityChips({ capabilities = [] }) {
  if (!capabilities.length) return null
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {capabilities.map((c) => (
        <Badge key={c} variant="secondary" className="font-normal">
          {c}
        </Badge>
      ))}
    </div>
  )
}

function ProductCard({ product }) {
  return (
    <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
      <div className="h-40 bg-slate-50 overflow-hidden relative">
        <img
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover"
        />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg leading-snug">{product.title}</CardTitle>
        <CardDescription className="line-clamp-3">{product.description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 mt-auto flex flex-col gap-4">
        <CapabilityChips capabilities={product.capabilities} />
        {product.purchaseUrl ? (
          <Button
            className="w-full"
            href={product.purchaseUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Purchase
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function HardwarePage() {
  const navigate = useNavigate()
  const [category, setCategory] = useState('all')

  const visibleCategories = useMemo(() => {
    if (category === 'all') return hardwareCategories
    const match = hardwareCategories.find((c) => c.id === category)
    return match ? [match] : []
  }, [category])

  const scrollToCatalog = () => {
    document.getElementById('compatible-hardware')?.scrollIntoView({ behavior: 'smooth' })
  }

  const jumpToCategory = (id) => {
    setCategory(id)
    scrollToCatalog()
  }

  return (
    <SiteLayout>
      <Seo
        title={hardwarePage.seoTitle}
        description={hardwarePage.seoDescription}
        path={hardwarePage.path}
        image={heroImageOf(hardwarePage)}
      />

      <PageHero
        title={hardwarePage.hero.title}
        description={hardwarePage.hero.description}
        image={heroImageOf(hardwarePage)}
        imageAlt="Restaurant POS hardware"
        actions={
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Button size="lg" onClick={scrollToCatalog}>Browse suggested products</Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/onboarding')}>
              Get Started
            </Button>
          </div>
        }
      />

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Smartphone, title: 'Any operating system', text: 'Runs on iOS and Android devices. Sync as many devices as you need.' },
              { icon: Tablet, title: 'All devices', text: 'Works on tablets and phones so your floor stays flexible.' },
              { icon: Receipt, title: 'For restaurants', text: 'Hardware chosen to look sharp on the counter and hold up in service.' },
            ].map((item) => (
              <Card key={item.title} className="text-center">
                <CardContent className="pt-6">
                  <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="compatible-hardware" className="py-20 bg-slate-50 scroll-mt-20">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Suggested compatible products
            </h2>
            <p className="text-lg text-muted-foreground">
              Tested and recommended for Tillio. Card payments use Stripe Terminal; printers and peripherals use common restaurant interfaces so you can buy what fits your counter.{' '}
              <Link to="/card-readers" className="text-primary hover:underline">
                Compare Stripe Terminal card readers
              </Link>
              {' '}or{' '}
              <Link to="/guides/connect-stripe-account" className="text-primary hover:underline">
                connect your Stripe account
              </Link>
              .
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
            <nav className="lg:sticky lg:top-24 self-start">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                POS Hardware
              </p>
              <ul className="space-y-1">
                <li>
                  <button
                    type="button"
                    onClick={() => setCategory('all')}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${
                      category === 'all'
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'hover:bg-white text-foreground'
                    }`}
                  >
                    All categories
                  </button>
                </li>
                {hardwareCategories.map((cat) => (
                  <li key={cat.id}>
                    <button
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${
                        category === cat.id
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-white text-foreground'
                      }`}
                    >
                      {cat.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="space-y-14 min-w-0">
              {visibleCategories.map((group) => (
                <div key={group.id} id={group.id}>
                  <h3 className="text-2xl font-bold mb-3">{group.title}</h3>
                  <p className="text-muted-foreground mb-6 max-w-3xl leading-relaxed">
                    {group.intro}
                  </p>
                  {group.items.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {group.items.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4">Payments</Badge>
              <h2 className="text-3xl font-bold mb-4">{hardwarePage.sections[0].title}</h2>
              <p className="text-lg text-muted-foreground mb-8">
                {hardwarePage.sections[0].description}
              </p>
              <h3 className="text-xl font-semibold mb-2">Stripe Terminal readers</h3>
              <p className="text-muted-foreground mb-4">
                Countertop readers connect over Wi‑Fi or ethernet for reliable high-volume service—tips, cards, and checkout without double entry.
              </p>
              <ul className="space-y-2 mb-6">
                {hardwarePage.sections[0].bullets.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" onClick={() => jumpToCategory('card-readers')}>
                  View card readers
                </Button>
                <Button variant="outline" onClick={() => navigate('/features/payments')}>
                  Learn about payments
                </Button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img src={sectionImageOf(hardwarePage, 0)} alt="Payment terminal" className="rounded-2xl shadow-2xl w-full max-w-md" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <img src={sectionImageOf(hardwarePage, 1)} alt="Tablet stand command station" className="rounded-2xl shadow-2xl w-full max-w-md" />
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-4">Command station</Badge>
              <h2 className="text-3xl font-bold mb-4">{hardwarePage.sections[1].title}</h2>
              <p className="text-lg text-muted-foreground mb-8">
                {hardwarePage.sections[1].description}
              </p>
              <ul className="space-y-2 mb-6">
                {hardwarePage.sections[1].bullets.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" onClick={() => jumpToCategory('stands')}>
                  View stands
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/features/customer-facing-display')}
                >
                  Customer display
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <h2 className="text-3xl font-bold mb-12 text-center">Tickets, kitchen display & receipts</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Printer className="h-8 w-8 text-primary" />
                  <CardTitle className="text-2xl">{hardwarePage.sections[2].title}</CardTitle>
                </div>
                <CardDescription>
                  {hardwarePage.sections[2].description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <img src={sectionImageOf(hardwarePage, 2)} alt="Kitchen display system" className="w-full rounded-lg mb-4" />
                <Button variant="outline" onClick={() => navigate('/features/kitchen-display')}>
                  Explore KDS
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <Mail className="h-8 w-8 text-primary" />
                  <CardTitle className="text-2xl">{hardwarePage.sections[3].title}</CardTitle>
                </div>
                <CardDescription>
                  {hardwarePage.sections[3].description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <img src={sectionImageOf(hardwarePage, 3)} alt="Receipt printer" className="w-full rounded-lg mb-4" />
                <ul className="space-y-2 mb-4">
                  {hardwarePage.sections[3].bullets.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{t}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" onClick={() => jumpToCategory('printers')}>
                  View printers
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{hardwarePage.sections[4].title}</h2>
              <div className="space-y-4 mb-8">
                {hardwarePage.sections[4].bullets.map((t) => (
                  <div key={t} className="flex items-center gap-3">
                    <Check className="h-6 w-6 text-primary shrink-0" />
                    <span className="text-lg">{t}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={() => navigate('/onboarding')}>Get Started</Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}>
                  View pricing
                </Button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img src={sectionImageOf(hardwarePage, 4)} alt="Modular restaurant hardware" className="rounded-2xl shadow-2xl w-full max-w-md" />
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        title="Build your restaurant setup"
        description="Tell us about your space and we’ll help you pick the right hardware mix."
      />
    </SiteLayout>
  )
}

export default HardwarePage
